import { useCallback, useEffect, useRef, useState } from "react";
import { publicApi } from "./orderApi";
import { RatePanel } from "./OrderExtras";
import { BranchAvatar, fmtDT } from "./orderUi";

const DISMISS_KEY = (slug) => `sc_fb_dismissed_${slug}`;
export const readDismissed = (slug) => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY(slug)) || "[]"); } catch { return []; } };
const dismissLocal = (slug, id) => { try { localStorage.setItem(DISMISS_KEY(slug), JSON.stringify([...new Set([...readDismissed(slug), id])].slice(-200))); } catch { /* storage blocked → server record still applies */ } };

/** "How was your food?" — automatic in-website invitation (branch toggle ON, served after enabling, unrated lines). Shown once per order until Later/Done. */
export const FeedbackInvite = ({ slug, token, o, logo, onLater }) => (
  <div className="os-sheet-backdrop" role="dialog" aria-modal="true" aria-label="How was your food?" data-testid="feedback-invite">
    <div className="os-sheet os-invite">
      <div className="os-invite-head"><h2>How was your food?</h2><p>Your feedback helps us serve you better.</p></div>
      <div className="os-invite-order"><BranchAvatar name={o.branch_name} image={o.branch_image_url} logo={logo} size={36} /><div><b>{o.branch_name}</b><div className="os-order-sub">{o.token_no != null ? `Token #${o.token_no}` : o.order_ref} · {fmtDT(o.created_at)}</div></div></div>
      <div className="os-invite-body"><RatePanel slug={slug} token={token} o={o} compact /></div>
      <div className="os-invite-foot"><button type="button" className="os-btn" onClick={onLater} data-testid="feedback-invite-later">Later</button><button type="button" className="os-btn primary" onClick={onLater} data-testid="feedback-invite-done">Done</button></div>
    </div>
  </div>
);

/** One invitation state shared by My Orders and Order Tracking: server flags (feedback_invite / feedback_dismissed / feedback_prompt_on)
 *  decide eligibility; Later/Done is persisted server-side AND locally; other tabs close via the storage event or the next poll. */
export const useFeedbackInvite = (slug, token, logo, screen) => {
  const [invite, setInvite] = useState(null);
  const ref = useRef(null); ref.current = invite;
  const offer = useCallback((orders) => {
    const cur = ref.current;
    if (cur) { const fresh = orders.find((o) => o.id === cur.id); if (fresh && (fresh.feedback_dismissed || fresh.feedback_prompt_on === false)) setInvite(null); return; }
    const dis = readDismissed(slug);
    const due = orders.find((o) => o.feedback_invite && !dis.includes(o.id));
    if (due) setInvite(due);
  }, [slug]);
  const close = useCallback(() => {
    const cur = ref.current; if (!cur) return;
    dismissLocal(slug, cur.id); setInvite(null);
    publicApi.post(`/${slug}/account/orders/${cur.id}/feedback-dismiss`, { token, screen }).catch(() => { /* local dismissal already applied; server retried on next Later */ });
  }, [slug, token, screen]);
  useEffect(() => {
    const h = (e) => { if (e.key === DISMISS_KEY(slug) && ref.current && readDismissed(slug).includes(ref.current.id)) setInvite(null); };
    window.addEventListener("storage", h); return () => window.removeEventListener("storage", h);
  }, [slug]);
  const sheet = invite ? <FeedbackInvite slug={slug} token={token} o={invite} logo={logo} onLater={close} /> : null;
  return { invite, offer, close, sheet };
};
